// Context for tracking the current route

import { createContext, useMemo, ReactNode } from 'react';
import { useLocation, matchPath } from 'react-router';
import { pageRoutes } from '@airhead-games/shared/configs';

export type RouteContextValue = {
  isRoomRoute: boolean;
  isGameRoute: boolean;
  isHostRoute: boolean;
  isPlayingRoute: boolean;
  pageTitle: string;
  normalizedPath: string;
  roomCode?: string;
  indexable: boolean;
};

export const RouteContext = createContext<RouteContextValue | undefined>(
  undefined
);

export const RouteProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();

  const value = useMemo(() => {
    const pathname = location.pathname;

    let matchingRoute = null;
    let routeParams = null;

    // Find matching route and extract parameters using matchPath
    for (const route of pageRoutes) {
      const match = matchPath({ path: route.path, end: true }, pathname);
      if (match) {
        matchingRoute = route;
        routeParams = match.params;
        break;
      }
    }

    const normalizedPath = matchingRoute?.normalizedPath || pathname;
    const pageTitle = matchingRoute?.pageTitle || 'Unknown Page';
    const roomCode = routeParams?.roomCode;
    const indexable = matchingRoute?.index !== false;

    return {
      isRoomRoute: normalizedPath === '/room',
      isGameRoute: normalizedPath === '/game',
      isHostRoute: normalizedPath === '/room' || normalizedPath === '/game',
      isPlayingRoute:
        normalizedPath === '/player' || normalizedPath === '/playing',
      pageTitle,
      normalizedPath,
      roomCode,
      indexable,
    };
  }, [location.pathname]);

  return (
    <RouteContext.Provider value={value}>
      {!value.indexable && <meta name="robots" content="noindex" />}
      {children}
    </RouteContext.Provider>
  );
};
